import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Building2, Scissors, Shield, Heart, ArrowUpRight } from 'lucide-react';
import { CONTACT_INFO, BRAND_INFO, CURRENCY, COUNTRY_OF_ORIGIN, SHIPPING_NOTICE, siteImages, ACCEPTED_CARDS_LABEL } from '../../data/mock';
import PaymentIcons from '../PaymentIcons';

/**
 * Footer - new light editorial layout for Stitch Your Dash
 * - Top: Brand tagline strip
 * - Main: 4-column light cream layout
 * - Bottom: Compliance + payment + corp info
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-warmWhite-200 text-warmBrown-900 overflow-hidden">
      {/* Tagline strip */}
      <div className="relative bg-warmBrown-900 text-cream-50">
        <div className="absolute inset-0 opacity-30">
          <img src={siteImages.featuredBackground} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-warmBrown-950 via-warmBrown-900/85 to-rose-900/40" />
        </div>
        <div className="relative container-page py-12 lg:py-14">
          <div className="grid lg:grid-cols-2 items-center gap-8">
            <div>
              <p className="eyebrow text-rose-300 mb-3">Made for Makers</p>
              <h3 className="font-display text-3xl lg:text-4xl leading-tight">
                Tools for cozy, <em className="italic-display text-rose-300 not-italic" style={{fontStyle: 'italic'}}>creative</em> sewing.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
              <Link
                to="/shop"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-rose-500 hover:bg-rose-600 text-white font-medium rounded-full transition-colors shadow-soft"
              >
                Shop the Collection
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-transparent border border-cream-200/40 hover:bg-cream-50/10 text-cream-50 font-medium rounded-full transition-colors"
              >
                Talk to Support
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main grid */}
      <div className="container-page py-14 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3 mb-5">
              <span className="font-display text-2xl text-warmBrown-950">
                Stitch <em className="italic-display text-rose-600 not-italic" style={{fontStyle: 'italic'}}>your</em> Dash
              </span>
            </Link>
            <p className="text-warmBrown-600 leading-relaxed max-w-md">
              {BRAND_INFO.tagline}. Cozy, modern sewing essentials for creators of all levels.
            </p>

            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-50 border border-rose-100">
              <Heart className="w-4 h-4 text-rose-500" />
              <span className="text-sm text-rose-700 font-medium">{SHIPPING_NOTICE}</span>
            </div>

            <div className="mt-6 space-y-1.5 text-sm text-warmBrown-600">
              <p>All prices in <span className="font-medium text-warmBrown-900">{CURRENCY}</span></p>
              <p>Country of Origin: <span className="font-medium text-warmBrown-900">{COUNTRY_OF_ORIGIN}</span></p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <PaymentIcons size="md" />
              <span className="flex items-center gap-1.5 text-xs text-warmBrown-500">
                <Shield className="w-3.5 h-3.5 text-sage-600" /> SSL Secured 256-bit Encryption
              </span>
            </div>
            <p className="mt-2 text-[11px] text-warmBrown-500">
              We accept {ACCEPTED_CARDS_LABEL}. <strong className="text-warmBrown-700">Discover is not accepted.</strong>
            </p>
          </div>

          {/* Shop links */}
          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-warmBrown-500 mb-5 font-semibold">Shop</h4>
            <ul className="space-y-3 text-warmBrown-700">
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">All Products</Link></li>
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">Quilting Tools</Link></li>
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">Fabric Scissors</Link></li>
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">Pattern Making</Link></li>
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">Pins & Clips</Link></li>
              <li><Link to="/shop" className="hover:text-rose-600 transition-colors">Storage Bags</Link></li>
            </ul>
          </div>

          {/* Help */}
          <div className="lg:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-warmBrown-500 mb-5 font-semibold">Help</h4>
            <ul className="space-y-3 text-warmBrown-700">
              <li><Link to="/shipping" className="hover:text-rose-600 transition-colors">Shipping</Link></li>
              <li><Link to="/returns" className="hover:text-rose-600 transition-colors">Returns & Refunds</Link></li>
              <li><Link to="/faq" className="hover:text-rose-600 transition-colors">FAQ</Link></li>
              <li><Link to="/guarantee" className="hover:text-rose-600 transition-colors">Our Guarantee</Link></li>
              <li><Link to="/contact" className="hover:text-rose-600 transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-warmBrown-500 mb-5 font-semibold">Visit & Contact</h4>

            <div className="space-y-4">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-3 group">
                <span className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500 transition-colors">
                  <Phone className="w-4 h-4 text-rose-600 group-hover:text-white transition-colors" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-warmBrown-500">Customer Care</span>
                  <span className="block font-display text-lg text-warmBrown-900 group-hover:text-rose-600 transition-colors">{CONTACT_INFO.phone}</span>
                </span>
              </a>

              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-3 group">
                <span className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0 group-hover:bg-rose-500 transition-colors">
                  <Mail className="w-4 h-4 text-rose-600 group-hover:text-white transition-colors" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-warmBrown-500">Email</span>
                  <span className="block text-warmBrown-900 group-hover:text-rose-600 transition-colors">{CONTACT_INFO.email}</span>
                </span>
              </a>

              <div className="flex items-start gap-3">
                <span className="w-9 h-9 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-rose-600" />
                </span>
                <span className="text-warmBrown-700 leading-relaxed">
                  <span className="block text-[11px] uppercase tracking-wider text-warmBrown-500">Corporate Address</span>
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}<br />
                  {CONTACT_INFO.address.country}
                </span>
              </div>

              <div className="flex items-start gap-3 pt-3 mt-3 border-t border-cream-200">
                <span className="w-9 h-9 rounded-full bg-warmBrown-900 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-cream-50" />
                </span>
                <span>
                  <span className="block text-[11px] uppercase tracking-wider text-warmBrown-500">Legal Entity (Operated by)</span>
                  <span className="block font-display text-base text-warmBrown-900">{CONTACT_INFO.corporateName}</span>
                  <span className="block text-xs text-warmBrown-600 mt-0.5">
                    d/b/a <strong className="text-warmBrown-800">{BRAND_INFO.name}</strong> &middot; {BRAND_INFO.domain}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom strip */}
      <div className="border-t border-cream-200/70 bg-cream-50">
        <div className="container-page py-5 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-warmBrown-600">
          <p>
            © {currentYear} {BRAND_INFO.name}. A {CONTACT_INFO.corporateName} brand. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-rose-600 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-rose-600 transition-colors">Terms</Link>
            <Link to="/shipping" className="hover:text-rose-600 transition-colors">Shipping</Link>
            <Link to="/returns" className="hover:text-rose-600 transition-colors">Returns</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
